import { API_URL, buildMockPromise, isMocksEnabled } from "./helpers";
import { BasicInfo, XPage, xpageNextOffset } from "./GenericModels";
import {
  Edit360DueDateArgs,
  ThreeSixtyAmendmentRow,
  ThreeSixtyFinishedRequest,
  ThreeSixtyFinishedRow,
  ThreeSixtyInvestigatingRow,
  ThreeSixtyQARow,
  ThreeSixtyQueueingRow,
  ThreeSixtyWebSearchRow,
} from "./ThreeSixty";
import { useMutation, useQuery } from "react-query";
import { useAppContext } from "../AppProviders";
import {
  getThreeSixtyAmendmentListMock,
  getThreeSixtyFinishedListMock,
  getThreeSixtyInvestigatingListMock,
  getThreeSixtyQAListMock,
  getThreeSixtyQueueingListMock,
  getThreeSixtyWebSearchListMock,
} from "../__mocks__/ThreeSixty";

/** Raw row from ``GET /lists/360/amend`` (``ReportSummaryItem`` JSON). */
type ReportSummaryItemJson = {
  report_uuid?: string | null;
  full_name?: string | null;
  client_report_id?: string | null;
  due_date?: string | null;
  finish_at?: string | null;
  company_uuid?: string | null;
  company_name?: string | null;
  blueprint_uuid?: string | null;
  blueprint_name?: string | null;
  behavior_kit_uuid?: string | null;
  behavior_kit_name?: string | null;
  workflow_run_uuid?: string | null;
  locked_by_name?: string | null;
  created_at?: string | null;
  step_payload?: Record<string, unknown> | null;
};

type ReportSummaryListResponseJson = {
  report_summaries?: ReportSummaryItemJson[] | null;
  total_rows?: number | null;
};

function isoOrEmpty(value: string | null | undefined): string {
  if (value == null || value === "") {
    return "";
  }
  return value;
}

function mapReportSummaryToAmendmentRow(
  item: ReportSummaryItemJson
): ThreeSixtyAmendmentRow {
  const sp = item.step_payload ?? {};
  const checkTypeUuid =
    (typeof sp.check_type_uuid === "string" && sp.check_type_uuid) ||
    (typeof sp.check_type_external_id === "string" &&
      sp.check_type_external_id) ||
    "";
  const checkTypeName =
    (typeof sp.check_type_name === "string" && sp.check_type_name) || "";

  return {
    request_uuid: item.report_uuid != null ? String(item.report_uuid) : "",
    name: item.full_name ?? "",
    custom_id: item.client_report_id ?? "",
    due_date: isoOrEmpty(item.due_date ?? undefined),
    finished_at: item.finish_at != null ? isoOrEmpty(item.finish_at) : null,
    company_uuid: item.company_uuid != null ? String(item.company_uuid) : "",
    company_name: item.company_name ?? "",
    blueprint_uuid:
      item.blueprint_uuid != null ? String(item.blueprint_uuid) : "",
    blueprint_name: item.blueprint_name ?? "",
    behavior_kit_uuid:
      item.behavior_kit_uuid != null ? String(item.behavior_kit_uuid) : null,
    behavior_kit_name: item.behavior_kit_name ?? null,
    ...(checkTypeUuid ? { check_type_uuid: checkTypeUuid } : {}),
    ...(checkTypeName ? { check_type_name: checkTypeName } : {}),
    workflow_run_uuid:
      item.workflow_run_uuid != null ? String(item.workflow_run_uuid) : "",
    locked_by: (item.locked_by_name ?? "").trim(),
    amendment_opened_at:
      item.created_at != null ? isoOrEmpty(item.created_at) : null,
  };
}

export function useGetThreeSixtyQueueingList() {
  const url = `${API_URL}/360/queueing`;
  const { requestApi } = useAppContext();
  const getMorePages = async (
    offset: number
  ): Promise<ThreeSixtyQueueingRow[]> => {
    const accumulator: ThreeSixtyQueueingRow[] = [];
    const response = await requestApi.get(`${url}?offset=${offset}`);
    const xpage: XPage<ThreeSixtyQueueingRow> = response.data;
    accumulator.push(...xpage.data);
    const nextOffset = xpageNextOffset(xpage, offset);
    if (nextOffset !== null) {
      const extra = await getMorePages(nextOffset);
      accumulator.push(...extra);
    }
    return accumulator;
  };

  const getThreeSixtyQueueingList = async (
    force: boolean
  ): Promise<ThreeSixtyQueueingRow[]> => {
    if (isMocksEnabled()) {
      return await getThreeSixtyQueueingListMock();
    }
    let full_dataset: ThreeSixtyQueueingRow[] = [];
    const response = await requestApi.get(`${url}?force=${force ? 1 : 0}`);
    const first_page: XPage<ThreeSixtyQueueingRow> = response.data;
    full_dataset.push(...first_page.data);
    const nextOffset = xpageNextOffset(first_page);
    if (nextOffset !== null) {
      const other_pages = await getMorePages(nextOffset);
      full_dataset.push(...other_pages);
    }
    return full_dataset;
  };
  const mutationResult = useMutation(
    (force: boolean) => getThreeSixtyQueueingList(force),
    {}
  );
  return { ...mutationResult };
}

export function useGetThreeSixtyWebSearchList() {
  const url = `${API_URL}/360/web_search`;
  const { requestApi } = useAppContext();
  const getMorePages = async (
    offset: number
  ): Promise<ThreeSixtyWebSearchRow[]> => {
    const accumulator: ThreeSixtyWebSearchRow[] = [];
    const response = await requestApi.get(`${url}?offset=${offset}`);
    const xpage: XPage<ThreeSixtyWebSearchRow> = response.data;
    accumulator.push(...xpage.data);
    const nextOffset = xpageNextOffset(xpage, offset);
    if (nextOffset !== null) {
      const extra = await getMorePages(nextOffset);
      accumulator.push(...extra);
    }
    return accumulator;
  };

  const getThreeSixtyWebSearchList = async (
    force: boolean
  ): Promise<ThreeSixtyWebSearchRow[]> => {
    if (isMocksEnabled()) {
      return await getThreeSixtyWebSearchListMock();
    }
    let full_dataset: ThreeSixtyWebSearchRow[] = [];
    const response = await requestApi.get(`${url}?force=${force ? 1 : 0}`);
    const first_page: XPage<ThreeSixtyWebSearchRow> = response.data;
    full_dataset.push(...first_page.data);
    const nextOffset = xpageNextOffset(first_page);
    if (nextOffset !== null) {
      const other_pages = await getMorePages(nextOffset);
      full_dataset.push(...other_pages);
    }
    return full_dataset;
  };
  const mutationResult = useMutation(
    (force: boolean) => getThreeSixtyWebSearchList(force),
    {}
  );
  return { ...mutationResult };
}

export function useGetThreeSixtyInvestigatingList() {
  const url = `${API_URL}/360/investigating`;
  const { requestApi } = useAppContext();
  const getMorePages = async (
    offset: number
  ): Promise<ThreeSixtyInvestigatingRow[]> => {
    const accumulator: ThreeSixtyInvestigatingRow[] = [];
    const response = await requestApi.get(`${url}?offset=${offset}`);
    const xpage: XPage<ThreeSixtyInvestigatingRow> = response.data;
    accumulator.push(...xpage.data);
    const nextOffset = xpageNextOffset(xpage, offset);
    if (nextOffset !== null) {
      const extra = await getMorePages(nextOffset);
      accumulator.push(...extra);
    }
    return accumulator;
  };

  const getThreeSixtyInvestigatingList = async (
    force: boolean
  ): Promise<ThreeSixtyInvestigatingRow[]> => {
    if (isMocksEnabled()) {
      return await getThreeSixtyInvestigatingListMock();
    }
    let full_dataset: ThreeSixtyInvestigatingRow[] = [];
    const response = await requestApi.get(`${url}?force=${force ? 1 : 0}`);
    const first_page: XPage<ThreeSixtyInvestigatingRow> = response.data;
    full_dataset.push(...first_page.data);
    const nextOffset = xpageNextOffset(first_page);
    if (nextOffset !== null) {
      const other_pages = await getMorePages(nextOffset);
      full_dataset.push(...other_pages);
    }
    return full_dataset;
  };
  const mutationResult = useMutation(
    (force: boolean) => getThreeSixtyInvestigatingList(force),
    {}
  );
  return { ...mutationResult };
}

export function useGetThreeSixtyQAList() {
  const url = `${API_URL}/360/qa`;
  const { requestApi } = useAppContext();
  const getMorePages = async (offset: number): Promise<ThreeSixtyQARow[]> => {
    const accumulator: ThreeSixtyQARow[] = [];
    const response = await requestApi.get(`${url}?offset=${offset}`);
    const xpage: XPage<ThreeSixtyQARow> = response.data;
    accumulator.push(...xpage.data);
    const nextOffset = xpageNextOffset(xpage, offset);
    if (nextOffset !== null) {
      const extra = await getMorePages(nextOffset);
      accumulator.push(...extra);
    }
    return accumulator;
  };

  const getThreeSixtyQAList = async (
    force: boolean
  ): Promise<ThreeSixtyQARow[]> => {
    if (isMocksEnabled()) {
      return await getThreeSixtyQAListMock();
    }
    let full_dataset: ThreeSixtyQARow[] = [];
    const response = await requestApi.get(`${url}?force=${force ? 1 : 0}`);
    const first_page: XPage<ThreeSixtyQARow> = response.data;
    full_dataset.push(...first_page.data);
    const nextOffset = xpageNextOffset(first_page);
    if (nextOffset !== null) {
      const other_pages = await getMorePages(nextOffset);
      full_dataset.push(...other_pages);
    }
    return full_dataset;
  };
  const mutationResult = useMutation(
    (force: boolean) => getThreeSixtyQAList(force),
    {}
  );
  return { ...mutationResult };
}

export function useGetThreeSixtyFinishedList() {
  const url = `${API_URL}/360/finished`;
  const { requestApi } = useAppContext();
  const getThreeSixtyFinishedList = async (
    args: ThreeSixtyFinishedRequest
  ): Promise<[number, ThreeSixtyFinishedRow[]]> => {
    if (isMocksEnabled()) {
      return await getThreeSixtyFinishedListMock();
    }
    const params: URLSearchParams = new URLSearchParams({});
    if (args.date) {
      // single date, use same value for min and max, BE will split into BOD and EOD (beginning/end of day) UTC
      params.set("min_date", args.date.toISOString().split("T")[0]);
      params.set("max_date", args.date.toISOString().split("T")[0]);
    }
    params.set("page", args.page.toString());
    if (args.sortField) {
      params.set("sort", (args.reverse ? "-" : "") + args.sortField);
    } else {
      params.set("sort", "-due_date");
    }
    if (args.company_uuids.length > 0) {
      params.set("company_uuids_csv", args.company_uuids.join(","));
    }
    if (args.blueprint_uuids.length > 0) {
      params.set("blueprint_uuids_csv", args.blueprint_uuids.join(","));
    }
    const namesCsv = (args.names ?? [])
      .map((name) => name.trim())
      .filter(Boolean)
      .join(",");
    if (namesCsv) {
      params.set("names_csv", namesCsv);
    }
    if (args.ref) {
      params.set("ref", args.ref);
    }
    const response = await requestApi.get(`${url}?${params.toString()}`);
    return [response.data[0], response.data[1]];
  };
  const mutationResult = useMutation(
    (args: ThreeSixtyFinishedRequest) => getThreeSixtyFinishedList(args),
    {}
  );
  return { ...mutationResult };
}

export type ThreeSixtyFinishedNameFilterOptionsRequest = {
  search: string;
  date?: Date;
  ref?: string;
  company_uuids: string[];
  blueprint_uuids: string[];
};

export function useGetThreeSixtyFinishedNameFilterOptions() {
  const url = `${API_URL}/360/finished/filter-options/names`;
  const { requestApi } = useAppContext();
  const getNameFilterOptions = async (
    args: ThreeSixtyFinishedNameFilterOptionsRequest
  ): Promise<string[]> => {
    if (isMocksEnabled()) {
      const [, rows] = await getThreeSixtyFinishedListMock();
      const search = args.search.toLowerCase();
      return Array.from(
        new Set(
          rows
            .map((row) => row.name)
            .filter((name) => name.toLowerCase().includes(search))
        )
      )
        .sort((a, b) => a.localeCompare(b))
        .slice(0, 20);
    }

    const params = new URLSearchParams({ q: args.search });
    if (args.date) {
      const date = args.date.toISOString().split("T")[0];
      params.set("min_date", date);
      params.set("max_date", date);
    }
    if (args.ref) {
      params.set("ref", args.ref);
    }
    if (args.company_uuids.length > 0) {
      params.set("company_uuids_csv", args.company_uuids.join(","));
    }
    if (args.blueprint_uuids.length > 0) {
      params.set("blueprint_uuids_csv", args.blueprint_uuids.join(","));
    }

    const response = await requestApi.get(`${url}?${params.toString()}`);
    return response.data;
  };

  return useMutation(
    (args: ThreeSixtyFinishedNameFilterOptionsRequest) =>
      getNameFilterOptions(args),
    {}
  );
}

export function useGetThreeSixtyAmendmentList() {
  const url = `${API_URL}/lists/360/amend`;
  const { requestApi } = useAppContext();

  const getThreeSixtyAmendmentList = async (
    _force: boolean
  ): Promise<ThreeSixtyAmendmentRow[]> => {
    if (isMocksEnabled()) {
      return await getThreeSixtyAmendmentListMock();
    }
    const pageSize = 100;
    const full_dataset: ThreeSixtyAmendmentRow[] = [];
    let offset = 0;

    while (true) {
      const response = await requestApi.get(
        `${url}?offset=${offset}&size=${pageSize}`
      );
      const body = response.data as ReportSummaryListResponseJson;
      const summaries = body.report_summaries ?? [];
      const total_rows = body.total_rows;

      for (const row of summaries) {
        full_dataset.push(mapReportSummaryToAmendmentRow(row));
      }
      if (summaries.length === 0) {
        break;
      }
      offset += summaries.length;
      if (typeof total_rows === "number" && full_dataset.length >= total_rows) {
        break;
      }
      if (total_rows == null && summaries.length < pageSize) {
        break;
      }
    }
    return full_dataset;
  };
  const mutationResult = useMutation(
    (force: boolean) => getThreeSixtyAmendmentList(force),
    {}
  );
  return { ...mutationResult };
}

export function useGetThreeSixtyCompanies() {
  const url = `${API_URL}/360/companies`;
  const { requestApi } = useAppContext();
  const getThreeSixtyCompanies = async (): Promise<BasicInfo[]> => {
    if (isMocksEnabled()) {
      return buildMockPromise([
        { name: "Company1", uuid: "couuid1" },
        { name: "Company2", uuid: "couuid2" },
        { name: "Company3", uuid: "couuid3" },
      ]);
    }
    const response = await requestApi.get(url);
    return response.data;
  };
  const mutationResult = useMutation(() => getThreeSixtyCompanies());
  return { ...mutationResult };
}

export function useEdit360DueDate() {
  const { requestApi } = useAppContext();
  const url = `${API_URL}/360/due_date`;
  const edit360DueDate = async (
    workflow_run_uuid: string,
    due_date: string
  ): Promise<void> => {
    const response = await requestApi.put(url, {
      workflow_run_uuid: workflow_run_uuid,
      due_date: due_date,
    });
    return response.data;
  };
  const mutationResult = useMutation(
    (args: Edit360DueDateArgs) =>
      edit360DueDate(args.workflow_run_uuid, args.due_date),
    {}
  );
  return { ...mutationResult };
}

export function useGetThreeSixtyBlueprints() {
  const url = `${API_URL}/360/blueprints`;
  const { requestApi } = useAppContext();

  const getThreeSixtyBlueprints = async (): Promise<BasicInfo[]> => {
    if (isMocksEnabled()) {
      return buildMockPromise([
        { name: "Test1", uuid: "bpuuid1" },
        { name: "Test2", uuid: "bpuuid2" },
        { name: "Test3", uuid: "bpuuid3" },
      ]);
    }
    // Backend filters based on authenticated user
    const response = await requestApi.get(url);
    return response.data;
  };

  return useQuery<BasicInfo[]>(
    ["threeSixtyBlueprints"],
    getThreeSixtyBlueprints,
    {
      staleTime: 15 * 60 * 1000, // 15 minutes
      cacheTime: 15 * 60 * 1000,
    }
  );
}
